import { usePersistFn } from '@psl/functions/hooks/usePersistFn';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Dimensions,
  LayoutChangeEvent,
  LayoutRectangle,
  Platform,
  View,
} from 'react-native';

export function useBoundingRect(
  enabled = true,
  options?: {
    onLayout?: (e: LayoutChangeEvent) => void;
  },
) {
  const ref = useRef<View>(null);
  const lastBounds = useRef<LayoutRectangle>();

  const [bounds, setBounds] = useState<LayoutRectangle>();

  const update = useCallback(() => {
    if (!enabled) return;

    ref.current?.measureInWindow((x, y, width, height) => {
      if (Platform.OS === 'web') {
        // fix the bounding rect when the page is scrolled
        // as result of the hardware keyboard appearing
        y += window.scrollY;
      }
      if (
        lastBounds.current?.x !== x ||
        lastBounds.current?.y !== y ||
        lastBounds.current?.width !== width ||
        lastBounds.current?.height !== height
      ) {
        lastBounds.current = { x, y, width, height };
        setBounds(lastBounds.current);
      }
    });
  }, [enabled]);

  useLayoutEffect(update, [update]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;

    // monitor screen/viewport size changes
    const listener = Dimensions.addEventListener('change', update);

    // When a text input is focused and the keyboard appears on iOS Safari,
    // the window's visual height changes (viewport shrinks), but iOS doesn't
    // fire the window resize event. This is intentional behavior from
    // Apple's side. To work around this, we listen to the scroll event
    // to detect the change in the window's visual height.
    document.addEventListener('scroll', update);
    visualViewport?.addEventListener('resize', update);

    return () => {
      listener.remove();
      document.removeEventListener('scroll', update);
      visualViewport?.removeEventListener('resize', update);
    };
  }, [enabled, update]);

  const onLayout = usePersistFn((e: LayoutChangeEvent) => {
    update();
    options?.onLayout?.(e);
  });

  return [ref, bounds, { update, onLayout }] as const;
}
